import { useParams, useSearchParams } from 'next/navigation';
import { GetServerSideProps } from 'next/types';
import nookies from 'nookies';
import { useEffect, useState, type JSX } from 'react';

import { default as B2BLanding } from '@/components/b2b/Landing';
import {
	PartnerDetails as B2BPartnerDetails,
	B2BReferer,
	PartnerCategory,
} from '@/components/b2b/shared/types';
import { default as DTCLanding } from '@/components/dtcPartnerships/Landing';
import { type PartnerDetails as DTCPartnerDetails } from '@/components/dtcPartnerships/shared/types';
import Layout from '@/components/layout/LayoutWrapper';
import { shapePartnerForParams } from '@/components/SimplifiedSignup/configs/Partnerships/shapePartnerForParams';
import { InitRequest, useUserState } from '@/hooks/store';
import { getPartner } from '@/server/initState/getPartner';
import { getUserData } from '@/utils/apiRequest/getUserData';
import { getCookie, setCookie } from '@/utils/cookies';
import { getPagesRouterSsrDirectOpts } from '@/utils/experiments/getPagesRouterSsrDirectOpts';
import getParamAsString from '@/utils/getParamsAsString';
import redirect from '@/utils/legacy/redirect';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

function isDTCPartner(rawPartner: B2BPartnerDetails | DTCPartnerDetails): rawPartner is DTCPartnerDetails {
	return (
		rawPartner?.category === PartnerCategory.D2C_LEAD_GEN_PARTNERSHIP ||
		rawPartner?.category === PartnerCategory.BRAND_PARTNERSHIP
	);
}

function Subscribe({ rawPartner }: { rawPartner: B2BPartnerDetails | DTCPartnerDetails }): JSX.Element {
	const searchParams = useSearchParams();
	const params = useParams();
	const user = useUserState();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const partnerSlug = getParamAsString(params?.partnerSlug);

	const linkTokenParam = searchParams?.get('calm-link-token') || getCookie('calm-link-token');
	const linkTokenString = Array.isArray(linkTokenParam) ? linkTokenParam[0] : linkTokenParam;
	useEffect(() => {
		if (linkTokenString) {
			setCookie('calm-link-token', linkTokenString, { expires: 1 }); // expire in 1 day
		}
	}, [linkTokenString]);

	const redeemSubCookie = getCookie('calm-page-identifier') === 'redeem-subscription';
	useEffect(() => {
		// If user is logged in and has subscription cookie, redeem through SSO and redirect to success view
		if (redeemSubCookie && user && linkTokenParam) {
			redirect('/redeem-subscription');
			return;
		}
		// TODO: refactor this code to follow best practices
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setIsLoading(false);
	}, [linkTokenParam, redeemSubCookie, user]);

	if (!partnerSlug || isLoading) {
		return <div />;
	}

	const pageName = isDTCPartner(rawPartner) ? 'dtc_partnership' : 'b2b';

	const landingProps: { linkToken?: string; partnerSlug: string; referer: B2BReferer } = {
		linkToken: linkTokenString,
		partnerSlug,
		referer: 'subscribe',
	};

	return (
		<Layout title="Calm" pageName={pageName}>
			{isDTCPartner(rawPartner) ? (
				<DTCLanding {...landingProps} rawPartner={rawPartner} />
			) : (
				<B2BLanding {...landingProps} rawPartner={rawPartner} />
			)}
		</Layout>
	);
}

export default Subscribe;

export const getServerSideProps: GetServerSideProps = async context => {
	const cookies = nookies.get({ req: context.req });
	const deviceId = cookies['x-device-id'] ?? '';
	const userData = getUserData({ req: context.req, deviceId });
	const ssrDirectOpts = await getPagesRouterSsrDirectOpts(context);
	const partner = await getPartner(context.query.partnerSlug, userData, ssrDirectOpts);
	const initialState = await getInitialServerState(
		context,
		[InitRequest.DEVICE, InitRequest.USER, InitRequest.PRICES],
		shapePartnerForParams(partner),
		ssrDirectOpts ?? 'skip-gate',
	);
	const initialStateWithPartner = {
		...initialState,
		partner,
	};

	return {
		props: {
			initialState: initialStateWithPartner,
			rawPartner: partner,
		},
	};
};
